import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../CDN';
import { LinkedInButton, TwitterButton } from '../components/buttons/SocialMediaButtons';
import { ContactUsMode } from '../components/forms/ContactForm';
import HeaderSection, { HeaderSectionDestinationUrl, HeaderSectionType } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import { StrapiApiClient } from '../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle } from '../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';
import AboutUsShowcase from '../widgets/showcases/AboutUsShowcase';

// Dynamically import with SSR disabled
const LearnMoreShowcase = dynamic(() => import('../widgets/showcases/LearnMoreShowcase'), { ssr: false });
const TeamMembersCards = dynamic(() => import('../widgets/cards/TeamMembersCards'), { ssr: false });
const SectionCoverParallax = dynamic(() => import('../components/interactive/SectionCoverParallax'), { ssr: false });

type AboutUsPageStaticProps = {
  articles: StrapiApiArticle[];
};

export const getStaticProps: GetStaticProps<AboutUsPageStaticProps> = async (context) => {
  const articles = await StrapiApiClient.getRandomMostRecentArticles();
  const props: AboutUsPageStaticProps = { articles };
  return withGlobalDefaultStaticProps(context, { props });
};

const AboutUsPage: NextPageWithLayout<AboutUsPageStaticProps> = (props) => {
  const { articles } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.AboutUs);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        type={HeaderSectionType.WhiteClouds}
        imageUrl={CDNAssets.getUrl('about-us-hero')}
        title="About Us"
        description={
          <div>
            <p className="text-3xl md:text-2xl">
              As pioneers in cutting-edge technology, we are dedicated to pushing the boundaries, giving solutions that transform industries
            </p>
            <div className="flex flex-col md:items-end">
              <div className="mt-10">
                <p className="text-2xl mb-4 font-bold">Find us</p>
                <div className="flex gap-2 ml-[50px] align-middle scale-[120%]">
                  <LinkedInButton fill="black" type="follow" />
                  <TwitterButton fill="black" type="follow" />
                </div>
              </div>
            </div>
          </div>
        }
        destinationUrl={HeaderSectionDestinationUrl.ContactUs}
      />
      <PageSection type={PageSectionType.White}>
        <SectionCoverParallax
          overlaySection={<AboutUsShowcase />}
          underlaySection={<h2 className="text-section-title text-center leading-none mb-[300px]">How it started</h2>}
        />
      </PageSection>
      <PageSection>
        <h2 className="text-section-title max-w-5xl leading-none ">Meet the team</h2>
        <TeamMembersCards
          members={[
            {
              name: 'Christos Karafeizis',
              role: 'Founder & CEO',
              imageUrl: CDNAssets.getUrl('about-us-team-member-ck'),
            },
            {
              name: 'Kostas Kaldis',
              role: 'Head of Product',
              imageUrl: CDNAssets.getUrl('about-us-team-member-kk'),
            },
            {
              name: 'Alex Tzialas',
              role: 'Head of AI',
              imageUrl: CDNAssets.getUrl('about-us-team-member-at'),
            },
            // {
            //   name: 'Stathis Kavros',
            //   role: 'DevOps Engineer',
            //   imageUrl: CDNAssets.getUrl('about-us-team-member-sk'),
            // },
            // {
            //   name: 'Spyros Tserkezos',
            //   role: 'Software Developer',
            //   imageUrl: CDNAssets.getUrl('about-us-team-member-sp'),
            // },
            // {
            //   name: 'Leon Vardinoyannis',
            //   role: 'Business Development Manager',
            //   imageUrl: CDNAssets.getUrl('about-us-team-member-lv'),
            // },
          ]}
        />
        <div className="max-w-4xl text-center mx-auto my-6">
          <p className="text-2xl">
            We are a passionate team driven by our AI passion and a shared mission to make work smarter and more efficient. Each team member
            brings unique expertise, contributing to the synergy that powers Gaspar AI&#39;s transformative solutions. Together, we&#39;re
            shaping the future of workplace productivity.
          </p>
        </div>
      </PageSection>
      <PageSection isNarrowTop={true}>
        <ContactUsWidget defaultMode={ContactUsMode.Connect} showAllModes={false} />
      </PageSection>
      <PageSection>
        <LearnMoreShowcase articles={articles} />
      </PageSection>
    </>
  );
};
AboutUsPage.displayName = 'AboutUsPage';
export default AboutUsPage;
